// WHICH FILE DOES A UniqueId PREFIX BELONG TO - and when the answer is "more
// than one", saying so instead of guessing.
//
// Owner, 2026-10-26: "in model view per project, the 3d view hides the tree
// completly for some users who click the eye on a tree parent to hide a full
// tree of a linked model/file needs fixing."
//
// A 36-character episode prefix is the head of an element's UniqueId, and the
// viewer uses it for two things on an export whose rows carry no file tag:
//   * the eye on a file's root row hides every element with one of that
//     file's prefixes (ViewerStateStore.hideDocument);
//   * a 3D click asks the ONE document the clicked uid's prefix names,
//     instead of parsing every document's records in turn.
//
// FILES SHARE PREFIXES. This is not hypothetical and it is not rare: the
// hidden-link rule in back/utils/modelSourceDocuments already had to be
// changed for it on 2026-09-18, because one shared prefix made SpaceX's
// DSI-MECH-R26 hide DSI-DWV-R26 as well (1 of its 88 prefixes was shared).
// The viewer built its map as prefix -> docId with a plain `set`, so a shared
// prefix was silently owned by whichever document was processed LAST:
//   * hiding a linked file could hide the host's elements with it - and the
//     host is most of the model, so the 3D goes blank. Which file wins
//     depends on the order the documents come back in, and that order
//     changes with the per-user model-source filter, so this struck SOME
//     users and not others on the same project.
//   * a click on a host element could ask only the link, find nothing, and
//     report "there is no model associated with this component".
//
// So a prefix with more than one claimant is SHARED and is used for neither.
// The eye then has nothing exclusive to hide and says so (TreeViewerItem
// already has those words), which is the third option beside hiding the
// wrong thing and doing nothing.

/**
 * @param {Array<{docId: string, prefixes: string[]}>} claims - one entry per
 *        document, with the prefixes GET /model-documents gave for it.
 * -> { owner: Map(prefix -> docId) for prefixes with ONE claimant,
 *      shared: Set(prefix) claimed by two or more,
 *      byDoc: Map(docId -> prefix[]) exclusive prefixes per document,
 *      claimed: Map(docId -> number) how many that document claimed in all }
 */
export function buildPrefixOwners(claims) {
  const claimants = new Map(); // prefix -> Set(docId)
  const claimed = new Map();
  (claims || []).forEach((c) => {
    const docId = c && c.docId;
    if (!docId) return;
    const list = (c.prefixes || []).map(String).filter(Boolean);
    claimed.set(docId, (claimed.get(docId) || 0) + list.length);
    list.forEach((p) => {
      if (!claimants.has(p)) claimants.set(p, new Set());
      claimants.get(p).add(docId);
    });
  });
  const owner = new Map();
  const shared = new Set();
  const byDoc = new Map();
  claimants.forEach((docs, prefix) => {
    if (docs.size !== 1) { shared.add(prefix); return; }
    const [only] = docs;
    owner.set(prefix, only);
    if (!byDoc.has(only)) byDoc.set(only, []);
    byDoc.get(only).push(prefix);
  });
  return { owner, shared, byDoc, claimed };
}

/** The prefixes that are this document's ALONE, in a stable order. */
export function exclusivePrefixes(owners, docId) {
  if (!owners || !docId) return [];
  return (owners.byDoc.get(docId) || []).slice().sort();
}

/**
 * The document a uid belongs to, or "" when its prefix is shared or unknown.
 * An empty answer means ASK EVERY DOCUMENT, which is slower and right; a
 * wrong answer means asking one document that does not hold the element and
 * concluding the model has no geometry for it.
 */
export function documentForUid(owners, uniqueId) {
  if (!owners || !uniqueId) return "";
  return owners.owner.get(String(uniqueId).slice(0, 36)) || "";
}

/**
 * Why the eye could not hide a file, for the row to say out loud. "" when it
 * can. Separating "this file shares all its prefixes with another" from "we
 * never learned this file's prefixes" matters: the first is a property of the
 * export, the second of the request that failed.
 */
export function whyNotHideable(owners, docId) {
  if (!owners) return "prefixes-unknown";
  if (exclusivePrefixes(owners, docId).length) return "";
  return (owners.claimed.get(docId) || 0) > 0 ? "prefixes-shared" : "prefixes-unknown";
}
